/**
 * 任务相关共享工具
 * 复用 MyDances 正在使用的 GET /api/v1/task 列表接口，供通知中心等场景使用
 */
import api from '../api';

/** 任务列表项（兼容 snake_case / camelCase 两种返回字段） */
export interface TaskItem {
  id?: string;
  task_id?: string;
  name?: string;
  task_name?: string;
  status?: string;
  task_status?: string;
  stage?: string;
  robot_model?: string;
  robotModel?: string;
  cover_url?: string | null;
  image_url?: string;
  imageUrl?: string;
  video_url?: string;
  created_at?: string;
  createdAt?: string;
  created_timestamp?: string;
  progress?: number;
  duration?: string | number;
  simulation_id?: string | null;
  media_id?: string | null;
  model_id?: string | null;
}

/** 归一化后的任务状态 */
export type TaskStatus = 'generating' | 'training' | 'pending' | 'success' | 'failed';

/** 任务状态 → 中文文案（通知中心展示用） */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  generating: '仿真视频生成中',
  training: '舞蹈模型训练中',
  pending: '排队等待中',
  success: '已完成',
  failed: '生成失败',
};

/** 任务 id（兼容 id / task_id） */
export const getTaskId = (task: TaskItem): string => String(task.id ?? task.task_id ?? '');

/** 归一化任务状态：success/failed 为终态，training/pending 保持原义，其余按 generating 处理 */
export function normalizeTaskStatus(raw: string | undefined): TaskStatus {
  const value = String(raw ?? '').toLowerCase();
  if (['success', 'succeed', 'finished', 'done', 'completed'].includes(value)) return 'success';
  if (['failed', 'fail', 'error'].includes(value)) return 'failed';
  if (['training'].includes(value)) return 'training';
  if (['pending', 'queued', 'waiting', 'queue'].includes(value)) return 'pending';
  return 'generating';
}

/** 是否为进行中任务（通知中心只展示这些） */
export function isTaskRunning(task: TaskItem): boolean {
  const status = normalizeTaskStatus(task.status ?? task.task_status);
  return status === 'generating' || status === 'training' || status === 'pending';
}

/** 获取当前用户的任务列表（与 MyDances 相同参数） */
export async function fetchTasks(params: { limit?: number; offset?: number } = {}): Promise<TaskItem[]> {
  const response = await api.task.list({
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
    origin_type: 'user',
  });
  return response.items;
}

/** 解析时间字符串，兼容 ISO 8601 与 'YYYY-MM-DD HH:mm:ss' 格式 */
const parseTime = (value: string): Date | null => {
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const matched = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{1,2}):(\d{1,2})/);
  if (matched) {
    const [, y, m, d, h, min, s] = matched;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), Number(s));
  }
  return null;
};

/** 相对时间文案：刚刚 / N分钟前 / N小时前 / N天前 / 原始时间 */
export function formatRelativeTime(timestamp: string | undefined): string {
  if (!timestamp) return '';
  const date = parseTime(timestamp);
  if (!date) return timestamp;
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return '刚刚';
  if (diffMin < 60) return `${diffMin}分钟前`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}小时前`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}天前`;
  return timestamp;
}
